import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { Button, Checkbox, Empty, Input, Select, Typography, theme } from 'antd';
// Internal path: antd does not re-export it. Clearing the status context is how a custom
// form control keeps a Form.Item error from painting every input inside it red.
import { FormItemInputContext } from 'antd/es/form/context';
import type React from 'react';
import { LazySortableRows } from '../../../../components/LazySortableRows.tsx';
import { LocalizationFieldType, type SpecimenLocalizationFieldInput } from '../../../../graphql/generated/graphql.ts';
import { useRowKeys } from '../../../../hooks/useRowKeys.ts';

/**
 * A field with this label is mapped to SPM.12 instead of SPM.14 by the HL7 generators. Keep in sync
 * with QUANTITY_LABEL in SpmLocalizationWriter.kt.
 */
const QUANTITY_LABEL = 'Menge';

const TYPE_OPTIONS = [
  { value: LocalizationFieldType.TEXT, label: 'Text' },
  { value: LocalizationFieldType.NUMBER, label: 'Zahl' },
];

/**
 * 0..n extra fields captured per Lokalisation. The config is per Material and applies to all of its
 * Lokalisationen. Identity is the label, order is the list position - neither has a surrogate id.
 */
export const LocalizationFieldsEditor: React.FC<{
  value?: SpecimenLocalizationFieldInput[];
  onChange?: (value: SpecimenLocalizationFieldInput[]) => void;
}> = ({ value = [], onChange }) => {
  const { token } = theme.useToken();
  const rowKeys = useRowKeys(value.length);

  const update = (index: number, patch: Partial<SpecimenLocalizationFieldInput>) =>
    onChange?.(value.map((it, i) => (i === index ? { ...it, ...patch } : it)));

  const remove = (index: number) => {
    rowKeys.remove(index);
    onChange?.(value.filter((_, i) => i !== index));
  };

  const add = () => onChange?.([...value, { label: '', inputType: LocalizationFieldType.TEXT, unit: null, mandatory: false }]);

  /** Case-insensitive, like SpecimenService.validateLocalizationFields and SpmLocalizationWriter. */
  const duplicate = (label: string, index: number) =>
    !!label.trim() && value.findIndex(it => it.label.trim().toLowerCase() === label.trim().toLowerCase()) !== index;

  const invalidLabel = (label: string, index: number) => !label.trim() || duplicate(label, index);

  // SPM.12 is a numeric HL7 field, so a "Menge" field must be a number - see SpmLocalizationWriter.kt
  const isQuantity = (label: string) => label.trim().toLowerCase() === QUANTITY_LABEL.toLowerCase();

  const listStyle = css`
    margin-bottom: ${token.marginXS}px;
  `;

  const labelStyle = css`
    flex: 1 1 auto;
    min-width: 0;
  `;

  const typeStyle = css`
    flex: 0 0 110px;
  `;

  const unitStyle = css`
    flex: 0 0 90px;
  `;

  const mandatoryStyle = css`
    flex: none;
    white-space: nowrap;
  `;

  const hintStyle = css`
    display: block;
    margin-bottom: ${token.marginXS}px;
    font-size: ${token.fontSizeSM}px;
  `;

  return (
    <div>
      <Typography.Text type="secondary" className={hintStyle}>
        Gilt für alle Lokalisationen dieses Materials. Ein Feld mit der Bezeichnung „{QUANTITY_LABEL}“ geht im HL7 als Probenmenge der
        Lokalisation (SPM.12, Wert und Einheit getrennt) und ist deshalb immer eine Zahl; alle anderen gehen als Text in deren
        Probenbeschreibung (SPM.14).
      </Typography.Text>
      <div className={listStyle}>
        {!value.length && <Empty description="Keine Zusatzfelder" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
        <FormItemInputContext.Provider value={{}}>
          <LazySortableRows
            items={value}
            keyOf={(_, index) => rowKeys.keyAt(index)}
            onReorder={(items, from, to) => {
              rowKeys.move(from, to);
              onChange?.(items);
            }}
            renderRow={(field, index) => (
              <>
                <Input
                  size="small"
                  className={labelStyle}
                  placeholder="Bezeichnung"
                  value={field.label}
                  status={invalidLabel(field.label, index) ? 'error' : undefined}
                  onChange={e =>
                    update(
                      index,
                      isQuantity(e.target.value)
                        ? { label: e.target.value, inputType: LocalizationFieldType.NUMBER }
                        : { label: e.target.value }
                    )
                  }
                />
                <Select
                  size="small"
                  className={typeStyle}
                  value={field.inputType}
                  options={TYPE_OPTIONS}
                  disabled={isQuantity(field.label)}
                  // a unit only makes sense for numbers - drop it when switching away
                  onChange={inputType => update(index, { inputType, unit: inputType === LocalizationFieldType.NUMBER ? field.unit : null })}
                />
                <Input
                  size="small"
                  className={unitStyle}
                  placeholder="Einheit"
                  disabled={field.inputType !== LocalizationFieldType.NUMBER}
                  value={field.unit ?? ''}
                  onChange={e => update(index, { unit: e.target.value || null })}
                />
                <Checkbox
                  className={mandatoryStyle}
                  checked={field.mandatory}
                  onChange={e => update(index, { mandatory: e.target.checked })}
                >
                  Pflicht
                </Checkbox>
                <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => remove(index)} />
              </>
            )}
          />
        </FormItemInputContext.Provider>
      </div>
      <Button size="small" icon={<PlusOutlined />} onClick={add}>
        Feld hinzufügen
      </Button>
    </div>
  );
};
